import moment from "moment";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import type {
  Audience,
  LeaderboardBoard,
  LeaderboardBoardsResponse,
  LeaderboardTemplate,
} from "../../../../types/leaderboard";
import {
  getRule,
  MIN_RANKED_MEMBERS,
  rankedRuleKeys,
} from "../leaderboard-rules";

const refresh = (body: Record<string, string>) =>
  submit("/actions/refresh", { method: "GET", body });

interface PublishStripProps {
  type: Audience;
  programId: string | null;
  programName: string | null;
  boardsData: LeaderboardBoardsResponse | undefined;
  activeTemplate: LeaderboardTemplate | undefined;
  templates: LeaderboardTemplate[];
  memberView: boolean;
  referAsClients: boolean;
  onSelectBoard: (board: LeaderboardBoard) => void;
}

/**
 * Strip under the audience tabs: what this audience (tab, optionally one
 * program) sees in their app, and the actions to change it. Every commit
 * names the audience of the active tab.
 */
const PublishStrip = ({
  type,
  programId,
  programName,
  boardsData,
  activeTemplate,
  templates,
  memberView,
  referAsClients,
  onSelectBoard,
}: PublishStripProps) => {
  const { t } = useTranslation("coaching-portal");
  const [editor, setEditor] = useState<"publish" | "rename" | null>(null);
  const [name, setName] = useState("");

  const {
    boards = [],
    audienceSize = 0,
    hiddenCount = 0,
    heldCount = 0,
    canPublish = false,
  } = boardsData || {};

  const tabLabel = t(`challengeReport.publish.tab.${type}`);
  // PUSH = subscribers, gym (PRESS + PERFORM) = members, PULL = athletes or
  // clients depending on the coach's preference.
  const audienceKey =
    type === "pull" ? (referAsClients ? "pullClients" : "pullAthletes") : type;
  const audienceName = programName
    ? t(`challengeReport.publish.programAudience.${audienceKey}`, {
        program: programName,
      })
    : t(`challengeReport.publish.audience.${audienceKey}`);
  // English sentences start with the audience ("Gym members see…").
  const Audience = audienceName.charAt(0).toUpperCase() + audienceName.slice(1);

  const sameAudience = (b: LeaderboardBoard) =>
    b.audience === type &&
    String(b.programId || "") === String(programId || "");
  const board = boards.find(sameAudience);
  const otherBoards = boards.filter(
    (b) => b.audience === type && !sameAudience(b)
  );
  const templateName = (id: string) =>
    templates.find((tpl) => String(tpl._id) === String(id))?.name;

  const showsActiveTemplate =
    board && activeTemplate && String(board.templateId) === activeTemplate._id;
  const onBoard = Math.max(0, audienceSize - hiddenCount - heldCount);

  const openEditor = (kind: "publish" | "rename") => {
    setName(
      kind === "rename"
        ? board.name
        : programName || activeTemplate?.name || tabLabel
    );
    setEditor(kind);
  };

  const publish = () => {
    submit("/actions/leaderboard-boards", {
      method: "POST",
      body: {
        templateId: activeTemplate._id,
        audience: type,
        ...(programId && { programId }),
        name: name.trim(),
      },
    });
    setEditor(null);
  };

  const updateBoard = (body: Record<string, string>) =>
    submit("/actions/leaderboard-boards/update", {
      method: "POST",
      body: { id: board._id, ...body },
    });

  // Delete removes the template itself (and every board using it).
  const deleteTemplate = () => {
    const liveElsewhere = boards.filter(
      (b) =>
        String(b.templateId) === activeTemplate._id &&
        b.state === "public" &&
        b.audience !== type
    );
    const warning = liveElsewhere.length
      ? `\n\n${t(
          "challengeReport.publish.deleteLiveWarning",
          "This template is also live on: {{audiences}}. Those boards will disappear from the app too.",
          {
            audiences: [
              ...new Set(
                liveElsewhere.map((b) =>
                  t(`challengeReport.publish.tab.${b.audience}`)
                )
              ),
            ].join(", "),
          }
        )}`
      : "";
    if (
      confirm(
        t(
          "challengeReport.publish.deleteConfirm",
          'Delete the template "{{name}}" permanently? This can\'t be undone.',
          { name: activeTemplate.name }
        ) + warning
      )
    ) {
      submit("/actions/challenge-report-templates/remove", {
        method: "POST",
        body: { id: activeTemplate._id },
      });
    }
  };

  const followColumns = activeTemplate
    ? rankedRuleKeys(activeTemplate).filter(
        (key) => getRule(activeTemplate.columnRules, key).window === "follow"
      ).length
    : 0;

  const previewButton = (
    <button
      type="button"
      data-button-type="secondary"
      disabled={!activeTemplate}
      title={
        activeTemplate
          ? undefined
          : t(
              "challengeReport.publish.saveFirst",
              "Save this view as a template to preview or publish it"
            )
      }
      onClick={() =>
        memberView
          ? refresh({ memberView: "" })
          : refresh({ memberView: "1", templateId: activeTemplate._id })
      }
    >
      {memberView
        ? t("challengeReport.publish.backToTable", "Back to my table")
        : t("challengeReport.publish.preview", "Preview in app")}
    </button>
  );

  let state: "off" | "live" | "turned-off";
  let eyebrow: string;
  let line: string;
  let meta: string | null = null;
  let actions: React.ReactNode;

  if (!board) {
    state = "off";
    eyebrow = t("challengeReport.publish.notShown", "Not shown in the app");
    line = t(
      "challengeReport.publish.noBoard",
      "{{Audience}} don't see a leaderboard in their app",
      { audience: audienceName, Audience }
    );
    actions = (
      <>
        {previewButton}
        {canPublish && activeTemplate && editor !== "publish" && (
          <button
            type="button"
            className="challenge-report__strip-cta"
            onClick={() => openEditor("publish")}
          >
            {t("challengeReport.publish.showThem", "Show them this template")}
          </button>
        )}
      </>
    );
  } else if (!showsActiveTemplate) {
    state = board.state === "public" ? "live" : "turned-off";
    eyebrow = t(
      "challengeReport.publish.otherTemplate",
      "Their board uses another template"
    );
    line = t(
      "challengeReport.publish.otherTemplateLine",
      '{{Audience}} see "{{board}}" ({{template}}), not this template',
      {
        audience: audienceName,
        Audience,
        board: board.name,
        template: templateName(board.templateId) || "—",
      }
    );
    actions = (
      <>
        <button
          type="button"
          data-button-type="secondary"
          onClick={() => onSelectBoard(board)}
        >
          {t("challengeReport.publish.backToBoard", "Back to their board")}
        </button>
        {previewButton}
        {canPublish && activeTemplate && editor !== "publish" && (
          <button
            type="button"
            className="challenge-report__strip-cta"
            onClick={() => openEditor("publish")}
          >
            {t("challengeReport.publish.replace", "Show them this instead")}
          </button>
        )}
      </>
    );
  } else if (board.state === "public") {
    state = "live";
    eyebrow = t("challengeReport.publish.on", "Public leaderboard turned ON");
    line = t(
      "challengeReport.publish.liveLine",
      '{{Audience}} see this as "{{board}}"',
      { audience: audienceName, Audience, board: board.name }
    );
    meta = t(
      "challengeReport.publish.liveMeta",
      "{{onBoard}} ranked · {{held}} on hold · {{hidden}} opted out · live since {{date}}",
      {
        onBoard,
        hidden: hiddenCount,
        held: heldCount,
        date: moment(board.publishedAt).format("D MMM"),
      }
    );
    actions = (
      <>
        {previewButton}
        {canPublish && (
          <>
            <button
              type="button"
              data-button-type="secondary"
              onClick={() => openEditor("rename")}
            >
              {t("challengeReport.publish.rename", "Rename")}
            </button>
            <button
              type="button"
              className="challenge-report__strip-turn-off"
              onClick={() => updateBoard({ state: "off" })}
            >
              {t("challengeReport.publish.turnOff", "Turn off")}
            </button>
          </>
        )}
      </>
    );
  } else {
    state = "turned-off";
    eyebrow = t("challengeReport.publish.off", "Public leaderboard turned OFF");
    line = t(
      "challengeReport.publish.offLine",
      '{{Audience}} don\'t see the leaderboard "{{board}}"',
      { audience: audienceName, Audience, board: board.name }
    );
    actions = (
      <>
        {previewButton}
        {canPublish && (
          <>
            <button
              type="button"
              data-button-type="secondary"
              onClick={() => openEditor("rename")}
            >
              {t("challengeReport.publish.rename", "Rename")}
            </button>
            <button
              type="button"
              className="challenge-report__strip-turn-on"
              onClick={() => updateBoard({ state: "public" })}
            >
              {t("challengeReport.publish.turnOn", "Turn on")}
            </button>
            <button
              type="button"
              data-button-type="danger"
              onClick={deleteTemplate}
            >
              {t("challengeReport.publish.delete", "Delete")}
            </button>
          </>
        )}
      </>
    );
  }

  return (
    <section className="challenge-report__strip" data-state={state}>
      <div className="challenge-report__strip-main">
        <span className="challenge-report__strip-dot" aria-hidden="true" />
        <div className="challenge-report__strip-text">
          <p className="challenge-report__strip-eyebrow">{eyebrow}</p>
          <p className="challenge-report__strip-line">{line}</p>
          {meta && <p className="challenge-report__strip-meta">{meta}</p>}
        </div>
        <div className="challenge-report__strip-actions">{actions}</div>
      </div>

      {otherBoards.length > 0 && (
        <div className="challenge-report__strip-chips">
          <span>
            {t("challengeReport.publish.alsoLive", "Also live for {{tab}}:", {
              tab: tabLabel,
            })}
          </span>
          {otherBoards.map((b) => (
            <button
              key={b._id}
              type="button"
              data-button-type="secondary"
              data-state={b.state}
              onClick={() => onSelectBoard(b)}
            >
              <span
                className="challenge-report__strip-dot"
                aria-hidden="true"
              />
              {b.name}
            </button>
          ))}
        </div>
      )}

      {board?.state === "public" && onBoard < MIN_RANKED_MEMBERS && (
        <p className="challenge-report__strip-min">
          <b>{t("challengeReport.publish.notRanking", "Not ranking yet.")}</b>{" "}
          {t(
            "challengeReport.publish.notRankingDetail",
            "{{Audience}} see “Leaderboard starts soon” until {{min}} are on the board — {{count}} so far.",
            {
              min: MIN_RANKED_MEMBERS,
              count: onBoard,
              audience: audienceName,
              Audience,
            }
          )}
        </p>
      )}

      {editor && (
        <div className="challenge-report__strip-editor">
          <div className="flex items-end gap-2">
            <label className="flex-1 mb-0!">
              {editor === "publish"
                ? t(
                    "challengeReport.publish.nameLabel",
                    "Board name {{audience}} will see",
                    { audience: audienceName, Audience }
                  )
                : t("challengeReport.publish.renameLabel", "Board name")}
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <button
              type="button"
              data-button-type="secondary"
              onClick={() => setEditor(null)}
            >
              {t("challengeReport.customize.cancel", "Cancel")}
            </button>
            {editor === "publish" ? (
              <button
                type="button"
                data-button-type="primary"
                disabled={!name.trim()}
                onClick={publish}
              >
                {t(
                  "challengeReport.publish.publishTo",
                  "Publish to {{count}} {{audience}}",
                  { count: audienceSize, audience: audienceName }
                )}
              </button>
            ) : (
              <button
                type="button"
                data-button-type="primary"
                disabled={!name.trim()}
                onClick={() => {
                  updateBoard({ name: name.trim() });
                  setEditor(null);
                }}
              >
                {t("challengeReport.publish.save", "Save")}
              </button>
            )}
          </div>
          {editor === "publish" && (
            <>
              {followColumns > 0 && (
                <p className="challenge-report__strip-info">
                  {t(
                    "challengeReport.publish.followNote",
                    "{{count}} columns follow your date range, so they rank {{audience}} on the last 12 months. Fine as is — set windows in Customize table for something shorter.",
                    { count: followColumns, audience: audienceName }
                  )}
                </p>
              )}
              {audienceSize < MIN_RANKED_MEMBERS && (
                <p className="challenge-report__strip-info">
                  {t(
                    "challengeReport.publish.smallAudience",
                    "Only {{count}} in this audience. You can publish, but it won't rank anyone until {{min}} are on it.",
                    { count: audienceSize, min: MIN_RANKED_MEMBERS }
                  )}
                </p>
              )}
              <p className="challenge-report__strip-fine">
                {t(
                  "challengeReport.publish.differences",
                  "{{Audience}} see this differently from your table: no photos, first name + initial, each column on its own window, top 100 only, no participant count. Preview in app shows exactly what they get.",
                  { audience: audienceName, Audience }
                )}
              </p>
            </>
          )}
        </div>
      )}
    </section>
  );
};

export default PublishStrip;
